import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { SavingIndicator } from '../../components/ui/SavingIndicator';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Clock,
  Calendar,
  CheckSquare,
  AlertCircle,
  Plus,
  Loader2,
  ArrowLeft,
  ChevronRight,
  Trash2,
  RefreshCw,
  FileText,
  ShieldCheck,
  Check
} from 'lucide-react';
import { cn } from '../../lib/utils';
import SubmissionGuideLive from './SubmissionGuideLive';
import PursuitLifecycleStepper from '../../components/pursuit/PursuitLifecycleStepper';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ComplianceTrackerLive() {
  const { workspaceId, pursuitId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders, authToken } = useAuth();
  // v3.11.8 — unified tab ("checklist" | "submission-day"). Deep-links from
  // the old /submission-guide route redirect here with ?tab=submission-day.
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') === 'submission-day' ? 'submission-day' : 'checklist';
  const setActiveTab = (t) => {
    const next = new URLSearchParams(searchParams);
    if (t === 'submission-day') next.set('tab', 'submission-day');
    else next.delete('tab');
    setSearchParams(next, { replace: true });
  };
  
  // State
  const [pursuit, setPursuit] = useState(null);
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [savingItems, setSavingItems] = useState(new Set());
  const [lastSaved, setLastSaved] = useState(null);
  
  // Modal state
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState({
    item_name: '',
    item_description: '',
    category: 'VOLUME',
    is_required: true
  });

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Fetch pursuit
        const pursuitRes = await fetch(`${API_URL}/api/pursuits/${pursuitId}`, {
          headers: getAuthHeaders()
        });
        if (pursuitRes.ok) {
          setPursuit(await pursuitRes.json());
        }
        
        // Fetch compliance items
        const itemsRes = await fetch(`${API_URL}/api/pursuits/${pursuitId}/compliance`, {
          headers: getAuthHeaders()
        });
        if (itemsRes.ok) {
          const data = await itemsRes.json();
          if (data.length === 0) {
            // Seed defaults if empty
            await seedDefaults();
          } else {
            setItems(data);
          }
        }
      } catch (err) {
        toast.error('Failed to load compliance data');
      } finally {
        setIsLoading(false);
      }
    };

    if (pursuitId) fetchData();
  }, [pursuitId, getAuthHeaders]);

  // Seed default items
  const seedDefaults = async () => {
    try {
      const res = await fetch(`${API_URL}/api/pursuits/${pursuitId}/compliance/seed-defaults`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        // Fetch seeded items
        const itemsRes = await fetch(`${API_URL}/api/pursuits/${pursuitId}/compliance`, {
          headers: getAuthHeaders()
        });
        if (itemsRes.ok) {
          setItems(await itemsRes.json());
        }
        toast.success('Default checklist items created');
      }
    } catch (err) {
      console.error('Failed to seed defaults:', err);
    }
  };

  // Handle checkbox toggle (auto-save)
  const handleToggle = async (itemId, currentState) => {
    const newState = !currentState;
    
    // Optimistic update
    setItems(prev => prev.map(item => 
      item.id === itemId ? { ...item, is_complete: newState } : item
    ));
    setSavingItems(prev => new Set([...prev, itemId]));
    
    try {
      const response = await fetch(`${API_URL}/api/pursuits/${pursuitId}/compliance/${itemId}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ is_complete: newState })
      });
      
      if (response.ok) {
        const data = await response.json();
        setLastSaved(data.saved_at);
        setItems(prev => prev.map(item => 
          item.id === itemId ? { ...item, is_complete: data.is_complete, completed_at: data.completed_at } : item
        ));
      } else {
        // Revert on error
        setItems(prev => prev.map(item => 
          item.id === itemId ? { ...item, is_complete: currentState } : item
        ));
        toast.error('Failed to save');
      }
    } catch (err) {
      setItems(prev => prev.map(item => 
        item.id === itemId ? { ...item, is_complete: currentState } : item
      ));
      toast.error('Failed to save');
    } finally {
      setSavingItems(prev => {
        const next = new Set(prev);
        next.delete(itemId);
        return next;
      });
    }
  };

  // Handle add item
  const handleAdd = async () => {
    if (!form.item_name.trim()) {
      toast.error('Item name is required');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/pursuits/${pursuitId}/compliance`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(form)
      });
      
      if (response.ok) {
        const newItem = await response.json();
        setItems(prev => [...prev, newItem]);
        toast.success('Item added');
        closeModal();
      }
    } catch (err) {
      toast.error('Failed to add item');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle delete
  const handleDelete = async (itemId) => {
    if (!window.confirm('Delete this checklist item?')) return;
    
    try {
      await fetch(`${API_URL}/api/pursuits/${pursuitId}/compliance/${itemId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      setItems(prev => prev.filter(item => item.id !== itemId));
      toast.success('Item deleted');
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const closeModal = () => {
    setIsAddModalOpen(false);
    setForm({ item_name: '', item_description: '', category: 'VOLUME', is_required: true });
  };

  // Group items by category
  // v3.11.8 — any unknown category falls through to OTHER instead of being
  // silently counted in totals but never rendered. Previously, defaults seeded
  // as DOCUMENT/FORMAT/REVIEW were invisible because the frontend only knew
  // VOLUME/FORMATTING/CERTIFICATION/DEADLINE/OTHER.
  const groupedItems = items.reduce((acc, item) => {
    const cat = item.category || 'OTHER';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  const categoryLabels = {
    VOLUME: 'Proposal Volumes',
    FORMATTING: 'Formatting Requirements',
    FORMAT: 'Formatting Requirements',          // backend alias (seed defaults)
    DEADLINE: 'Key Deadlines',
    CERTIFICATION: 'Certifications & Forms',
    DOCUMENT: 'Required Documents',             // backend alias (seed defaults)
    REVIEW: 'Review & QA Gates',                // backend alias (seed defaults)
    OTHER: 'Other Items'
  };

  // Known render order — then append any categories found in data that
  // aren't in this list (future-proof so the bug doesn't recur).
  const knownOrder = ['VOLUME', 'DOCUMENT', 'FORMATTING', 'FORMAT', 'CERTIFICATION', 'DEADLINE', 'REVIEW', 'OTHER'];
  const unknownCats = Object.keys(groupedItems).filter(c => !knownOrder.includes(c));
  const categoryOrder = [...knownOrder, ...unknownCats];

  // Stats
  // v3.10.10: the headline percentage is anchored to REQUIRED items
  // because that's what determines submission readiness. Optional items
  // are tracked but should not pull the gauge down — completing all 8
  // required items must read as "100% Complete · Submission Ready" even
  // when 2 optional items remain unchecked. Pre-3.10.10 we used
  // (completedCount / totalCount) which made a fully-compliant pursuit
  // look only 80% done.
  const completedCount = items.filter(i => i.is_complete).length;
  const totalCount = items.length;
  const requiredComplete = items.filter(i => i.is_required && i.is_complete).length;
  const requiredTotal = items.filter(i => i.is_required).length;
  const optionalComplete = completedCount - requiredComplete;
  const optionalTotal = totalCount - requiredTotal;

  // Pick a denominator: required-first, fall back to all items if a
  // pursuit has zero required items (otherwise the bar would be stuck
  // at 0% / NaN forever).
  const progressNumerator = requiredTotal > 0 ? requiredComplete : completedCount;
  const progressDenominator = requiredTotal > 0 ? requiredTotal : totalCount;
  const progressPct = progressDenominator > 0
    ? Math.round((progressNumerator / progressDenominator) * 100)
    : 0;
  const submissionReady = requiredTotal > 0 && requiredComplete === requiredTotal;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div data-testid="compliance-tracker-live" className="min-h-screen bg-slate-50">
      {/* Header — v3.18.20 centered to max-w-7xl */}
      <div className="bg-white border-b border-slate-200 px-8 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-6 px-2"
                onClick={() => navigate(`/${workspaceId}/pursuit/${pursuitId}/gaps`)}
              >
                <ArrowLeft className="w-3 h-3 mr-1" />
                Gap Analysis
              </Button>
              <ChevronRight className="w-4 h-4" />
              <span className="text-slate-700">Compliance Tracker</span>
            </div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Submission Compliance
            </h1>
            {pursuit && (
              <p className="text-sm text-slate-500 mt-1">
                {pursuit.opportunity_name}
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            {savingItems.size > 0 ? (
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </div>
            ) : lastSaved ? (
              <div className="text-sm text-emerald-600">
                Saved
              </div>
            ) : null}
            <Button
              className="bg-teal-700 hover:bg-teal-800 text-white"
              onClick={() => setIsAddModalOpen(true)}
              data-testid="add-compliance-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Item
            </Button>
          </div>
        </div>
      </div>

      <div className="px-8">
        <div className="max-w-7xl mx-auto">
          <PursuitLifecycleStepper />
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="bg-white border-b border-slate-200 px-8">
          <div className="max-w-7xl mx-auto">
            <TabsList className="bg-transparent h-auto p-0 gap-1">
              <TabsTrigger
                value="checklist"
                data-testid="tab-checklist"
                className="data-[state=active]:bg-slate-900 data-[state=active]:text-white rounded-b-none px-4 py-2"
              >
                Checklist
              </TabsTrigger>
              <TabsTrigger
                value="submission-day"
                data-testid="tab-submission-day"
                className="data-[state=active]:bg-slate-900 data-[state=active]:text-white rounded-b-none px-4 py-2"
              >
                Submission Day
              </TabsTrigger>
            </TabsList>
          </div>
        </div>

        <TabsContent value="checklist" className="mt-0">
          <div className="p-8">
            <div className="max-w-7xl mx-auto">
        {/* Progress Banner — anchored on REQUIRED items; flips to
            emerald "Submission Ready" the moment all required boxes are
            checked, regardless of optional items. */}
        <Card
          className={cn(
            'border-0 shadow-lg mb-6 transition-colors',
            submissionReady ? 'bg-emerald-600' : 'bg-blue-600',
          )}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between text-white">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-xl font-semibold">Compliance Progress</h3>
                  {submissionReady && (
                    <Badge
                      data-testid="submission-ready-badge"
                      className="bg-white/20 text-white border-white/30 hover:bg-white/30 gap-1"
                    >
                      <ShieldCheck className="w-3 h-3" />
                      Submission Ready
                    </Badge>
                  )}
                </div>
                <p className={submissionReady ? 'text-emerald-100' : 'text-blue-100'}>
                  {requiredTotal > 0
                    ? <>{requiredComplete} of {requiredTotal} required items complete{optionalTotal > 0 ? <> · {optionalComplete}/{optionalTotal} optional</> : null}</>
                    : <>{completedCount} of {totalCount} items complete</>}
                </p>
              </div>
              <div className="text-right">
                <p className="text-5xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {progressPct}%
                </p>
                <p className={cn('text-sm', submissionReady ? 'text-emerald-100' : 'text-blue-100')}>
                  {submissionReady ? 'Submission Ready' : 'Required Complete'}
                </p>
              </div>
            </div>
            {/* Progress bar — driven by the required-first numerator. */}
            <div className={cn(
              'mt-4 h-2 rounded-full overflow-hidden',
              submissionReady ? 'bg-emerald-500/50' : 'bg-blue-500/50',
            )}>
              <div
                className="h-full bg-white transition-all duration-500"
                style={{ width: `${progressPct}%` }}
              />
            </div>
            {/* v3.11.8 — per-category breakdown strip. Makes the relationship
                between the 4/8 headline and the category cards below explicit,
                so users know exactly where the remaining required items live. */}
            {Object.keys(groupedItems).length > 0 && (
              <div
                className="mt-4 pt-4 border-t border-white/20 flex flex-wrap gap-x-5 gap-y-2 text-xs"
                data-testid="compliance-category-breakdown"
              >
                {categoryOrder.filter(c => groupedItems[c]?.length).map(cat => {
                  const rows = groupedItems[cat];
                  const required = rows.filter(r => r.is_required);
                  const complete = required.filter(r => r.is_complete);
                  const allReqComplete = required.length > 0 && complete.length === required.length;
                  return (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => {
                        const el = document.querySelector(`[data-testid="compliance-category-${cat}"]`);
                        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }}
                      data-testid={`breakdown-chip-${cat}`}
                      className={cn(
                        'inline-flex items-center gap-1.5 px-2 py-1 rounded border border-white/20 text-white/90 hover:bg-white/10 transition-colors',
                        allReqComplete && 'bg-white/15'
                      )}
                      title={`Jump to ${categoryLabels[cat] || cat}`}
                    >
                      {allReqComplete && <Check className="w-3 h-3" />}
                      <span className="font-medium">{categoryLabels[cat] || cat}:</span>
                      <span className="tabular-nums">
                        {required.length > 0
                          ? `${complete.length}/${required.length} req`
                          : `${rows.filter(r => r.is_complete).length}/${rows.length}`}
                      </span>
                      {rows.length > required.length && (
                        <span className="text-white/60 text-[11px]">
                          · +{rows.length - required.length} opt
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Compliance Matrix moved to Intake/Ingestion (created at pursuit time).
            This slot is reserved for the upcoming review-gate compliance check. */}
        <div className="mb-6" data-testid="review-gate-placeholder">
          <Card className="border-dashed border-2 border-slate-200 bg-slate-50/40">
            <CardContent className="py-10 text-center">
              <ShieldCheck className="w-8 h-8 text-slate-300 mx-auto mb-3" />
              <h3 className="text-sm font-semibold text-slate-600">Review-Gate Compliance Check</h3>
              <p className="text-xs text-slate-400 mt-1 max-w-md mx-auto">
                The Compliance Matrix now lives in Intake (created when you pursue an
                opportunity). This stage will host the review-gate compliance check —
                run a compliance pass against your draft volumes at each review gate.
                Coming soon.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* v3.10.14: Submission Compliance section header above the Section L checklist
            so it's visually distinct from the Compliance Matrix above. */}
        <div className="flex items-center justify-between mb-3 pt-2">
          <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Submission Compliance
          </h2>
          <span className="text-xs text-slate-500">Section L checklist · page-limit tracking</span>
        </div>

        {/* Checklist by Category */}
        <div className="space-y-6">
          {categoryOrder.map(category => {
            const categoryItems = groupedItems[category];
            if (!categoryItems || categoryItems.length === 0) return null;
            
            return (
              <Card key={category} data-testid={`compliance-category-${category}`} className="border-slate-200 shadow-sm">
                <CardHeader className="pb-3 border-b border-slate-100">
                  <CardTitle className="text-base font-semibold flex items-center gap-2">
                    <CheckSquare className="w-4 h-4 text-slate-500" />
                    {categoryLabels[category] || category}
                    <Badge variant="secondary" className="ml-2 text-xs">
                      {categoryItems.filter(i => i.is_complete).length}/{categoryItems.length}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-3">
                  <div className="space-y-2">
                    {categoryItems.map((item) => (
                      <div 
                        key={item.id} 
                        className={cn(
                          "p-4 rounded-lg border border-slate-100 flex items-start gap-3 hover:bg-slate-50 hover:border-slate-200 transition-colors",
                          item.is_complete && "bg-slate-50/50"
                        )}
                        data-testid={`compliance-item-${item.id}`}
                      >
                        <Checkbox 
                          checked={item.is_complete}
                          onCheckedChange={() => handleToggle(item.id, item.is_complete)}
                          disabled={savingItems.has(item.id)}
                          className={cn(
                            "mt-0.5",
                            item.is_complete && "bg-blue-600 border-blue-600 text-white"
                          )}
                          data-testid={`checkbox-${item.id}`}
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className={cn(
                              "font-medium",
                              item.is_complete ? "text-slate-500 line-through" : "text-slate-900"
                            )}>
                              {item.item_name}
                            </p>
                            {item.is_required && (
                              <Badge className="bg-red-50 text-red-600 border border-red-200 text-xs">
                                REQUIRED
                              </Badge>
                            )}
                            {savingItems.has(item.id) && (
                              <Loader2 className="w-3 h-3 animate-spin text-blue-500" />
                            )}
                          </div>
                          {item.item_description && (
                            <p className="text-sm text-slate-500 mt-1">{item.item_description}</p>
                          )}
                          {item.is_complete && item.completed_at && (
                            <p className="text-xs text-emerald-600 mt-1">
                              Completed {new Date(item.completed_at).toLocaleString()}
                            </p>
                          )}
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDelete(item.id)}
                          className="text-slate-400 hover:text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {items.length === 0 && (
          <Card className="border-slate-200">
            <CardContent className="p-8 text-center">
              <CheckSquare className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 mb-4">No compliance items yet</p>
              <Button onClick={() => setIsAddModalOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add First Item
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Navigation — v3.18.16: Compliance Matrix is now BEFORE Proposal,
            so the canonical forward target is Proposal Workspace. Back goes
            to Strategy (the step that informs the matrix). */}
        <div className="flex items-center justify-between mt-6 p-4 bg-white rounded-lg border border-slate-200">
          <Button 
            variant="outline"
            onClick={() => navigate(`/${workspaceId}/pursuit/${pursuitId}/strategy`)}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Strategy
          </Button>
          <Button
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            onClick={() => navigate(`/${workspaceId}/pursuit/${pursuitId}/proposal`)}
          >
            <FileText className="w-4 h-4 mr-2" />
            Proceed to Proposal Workspace
          </Button>
        </div>
      </div>
        </div>
        </TabsContent>

        {/* v3.11.8 — Submission Day tab reuses SubmissionGuideLive in
            embedded mode so there's zero data/code duplication. */}
        <TabsContent value="submission-day" className="mt-0" data-testid="tab-panel-submission-day">
          <div className="max-w-7xl mx-auto">
            <SubmissionGuideLive embedded />
          </div>
        </TabsContent>
      </Tabs>

      {/* Add Item Modal */}
      <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
        <DialogContent className="sm:max-w-md" data-testid="add-compliance-modal">
          <DialogHeader>
            <DialogTitle>Add Compliance Item</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Item Name *</Label>
              <Input
                value={form.item_name}
                onChange={(e) => setForm({...form, item_name: e.target.value})}
                placeholder="e.g., Volume I: Technical Proposal"
                data-testid="compliance-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={form.item_description}
                onChange={(e) => setForm({...form, item_description: e.target.value})}
                placeholder="Additional details..."
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="VOLUME">Volume</SelectItem>
                    <SelectItem value="FORMATTING">Formatting</SelectItem>
                    <SelectItem value="CERTIFICATION">Certification</SelectItem>
                    <SelectItem value="DEADLINE">Deadline</SelectItem>
                    <SelectItem value="OTHER">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Required?</Label>
                <Select 
                  value={form.is_required ? 'yes' : 'no'} 
                  onValueChange={(v) => setForm({...form, is_required: v === 'yes'})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Required</SelectItem>
                    <SelectItem value="no">Optional</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={closeModal}>Cancel</Button>
            <Button 
              onClick={handleAdd}
              disabled={isSubmitting}
              className="bg-teal-700 hover:bg-teal-800 text-white"
              data-testid="compliance-submit-btn"
            >
              {isSubmitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Add Item
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
